/**
 * simAvailability — สถานะ SIM ที่เลือกเป็น "SIM ใหม่" ตอนเปลี่ยนอุปกรณ์/SIM ได้
 *
 * ต้องตรงกับ backend `crud/device_replacement.py` (new_sim.status ∈ {AVAILABLE, ACTIVE, INACTIVE}
 * ไม่งั้น 400 "SIM ที่เลือกไม่พร้อมใช้งาน") — user bug Tiwakorn 2026-08-30 (maintenance #309):
 * picker เดิมกรองออกแค่ EXPIRED/SUSPENDED → SIM IN_USE 681 เบอร์บน prod โผล่ให้เลือก ทั้งที่ว่างจริงแค่ 4
 * ผู้ใช้เลือกซิมที่ลูกค้าอีกรายใช้อยู่แล้วโดน 400 ทุกครั้ง
 */
export const REPLACEMENT_SELECTABLE_SIM_STATUSES: ReadonlySet<string> = new Set([
  'AVAILABLE',
  'ACTIVE', // deprecated alias — backend ยังรับช่วง transition
  'INACTIVE',
]);

/** true = โชว์ใน dropdown "เลือก SIM ใหม่" ได้ · สถานะว่าง = fail-open ตาม backend (`if new_sim.status and ...`) */
export function isSimSelectableForReplacement(status: string | null | undefined): boolean {
  if (status === null || status === undefined || String(status).trim() === '') return true;
  return REPLACEMENT_SELECTABLE_SIM_STATUSES.has(String(status).trim().toUpperCase());
}

/* ==========================================================================
 * ด่าน L2 ฝั่งซิม — ตัดซิมที่ "ยังมีใบตามเก็บค้าง" ออกจากตัวเลือก
 *
 * **คู่ขนานตรง ๆ ของ `utils/devicePickerFilter.ts`** (Lane D, 9 ก.ย. 2026) — วางไว้ไฟล์นี้
 * เพื่อให้กติกา "ซิมใบไหนเลือกได้" อยู่ที่เดียว ไม่ต้องไล่หาสองที่
 *
 * ปัญหา: ด่าน L2 (`crud/rental._assert_no_open_return_item`) ปฏิเสธ **ทั้งเครื่องและซิม**
 * ที่ยังมีใบตามเก็บเปิดค้าง แต่ฝั่งจอกรองไว้แค่ฝั่งเครื่อง · picker ซิมกรองด้วยสถานะอย่างเดียว
 * ซึ่งไม่รู้เรื่องใบตามเก็บเลย → prod มีซิม **AVAILABLE 16 · INACTIVE 8** ที่โผล่ใน dropdown
 * แล้วกดบันทึกโดน 400 โดยไม่มีอะไรบอกเหตุผล เลือกเบอร์ถัดไปก็เจอซ้ำเป็นชุด
 *
 * กติกา (เหมือนฝั่งเครื่องทุกข้อ):
 * - ซิมที่ `has_pending_return` เป็นจริง → **ไม่เสนอ** (ซ่อน ไม่ใช่ disabled — parity กับ picker
 *   ใบงาน/ใบเบิกที่ backend ตัดออกตั้งแต่ SQL · จอที่ค้นแล้วไม่เจอให้ใช้ `simPendingReturnReason`
 *   อธิบายผ่าน emptyMessage)
 * - `keepIds` = ซิมที่อยู่ในสัญญา/ฟอร์มใบนี้อยู่แล้ว → **คงไว้เสมอ** ไม่งั้นแถวเดิมของฟอร์มแก้ไข
 *   กลับเป็น "-- เลือก SIM --" ทั้งที่สัญญาถือซิมนั้นอยู่จริง (ด่าน backend ก็ดูเฉพาะใบของ
 *   "สัญญาอื่น" เหมือนกัน)
 * - ธงว่าง/undefined (backend รุ่นเก่า) = **ถือว่าเลือกได้** (fail-open ตรงกับ default False
 *   ของ schema) — ไม่งั้น FE ใหม่ + BE เก่า = เลือกซิมไม่ได้เลยสักใบ
 * ========================================================================== */

export interface SimPendingReturnLike {
  id: number;
  has_pending_return?: boolean | null;
  pending_return_count?: number | null;
}

type SimIdLike = number | string | '' | null | undefined;

/**
 * true = ซิมนี้ยังมีรายการตามเก็บที่ยังไม่ปิด (PENDING/SCHEDULED/IN_PROGRESS)
 * รับ object อะไรก็ได้ — payload ซิมแต่ละหน้ามี type ท้องถิ่นของตัวเอง บางตัวยังไม่ประกาศธงนี้
 */
export function simHasPendingReturn(sim: object | null | undefined): boolean {
  return (sim as Partial<SimPendingReturnLike> | null | undefined)?.has_pending_return === true;
}

const toSimIdSet = (ids: Iterable<SimIdLike>): Set<number> => {
  const set = new Set<number>();
  for (const id of ids) {
    const n = typeof id === 'number' ? id : Number(id);
    if (Number.isFinite(n) && n > 0) set.add(n);
  }
  return set;
};

/**
 * ตัดซิมที่มีใบตามเก็บค้างออก ยกเว้น id ใน `keepIds` (ซิมที่อยู่ในสัญญา/ฟอร์มนี้อยู่แล้ว)
 * — คืน array ใหม่เสมอ ไม่แก้ของเดิม · ลำดับคงเดิม
 */
export function excludeSimsPendingReturn<T extends SimPendingReturnLike>(
  sims: readonly T[],
  keepIds: Iterable<SimIdLike> = [],
): T[] {
  const keep = toSimIdSet(keepIds);
  return sims.filter((s) => !simHasPendingReturn(s) || keep.has(s.id));
}

/**
 * ข้อความอธิบายว่าทำไมซิมนี้ "มีอยู่แต่เลือกไม่ได้" — ใช้ใน emptyMessage ของ picker
 * คืน null เมื่อซิมไม่มีใบค้าง (ผู้เรียกไปเช็คเหตุอื่นต่อ)
 */
export function simPendingReturnReason(
  sim: SimPendingReturnLike | null | undefined,
  label?: string,
): string | null {
  if (!simHasPendingReturn(sim)) return null;
  const n = sim?.pending_return_count || 0;
  const who = (label ?? '').trim() || 'ซิมนี้';
  const count = n > 0 ? ` ${n} รายการ` : '';
  return (
    `พบ ${who} แต่เลือกไม่ได้ — ยังมีรายการตามเก็บที่ยังไม่ปิด${count} (รอรับคืน · ซิมอาจยังอยู่กับลูกค้า) · ` +
    'บันทึกรับคืนที่เมนู "ติดตามการคืนอุปกรณ์" หรือยกเลิกรายการตามเก็บก่อน แล้วกลับมาเลือกใหม่'
  );
}

/* ==========================================================================
 * ยืมซิม (23 ก.ย. 2026): ซิม ISSUED = ถูกยืมไปใช้ภายในตามใบเบิก-ยืม
 * ไม่อยู่ใน allow-list ข้างบน (จึงเลือกไม่ได้โดยอัตโนมัติ) — helper นี้แค่บอก "ทางออก" ให้ชัด
 * ========================================================================== */

export interface SimIssuedLike {
  status?: string | null;
  current_stock_issue_number?: string | null;
  issued_to_display?: string | null;
}

/** ข้อความอธิบายเมื่อซิมถูกยืมอยู่ — คืน null เมื่อไม่ใช่ ISSUED */
export function simIssuedReason(sim: SimIssuedLike | null | undefined, label?: string): string | null {
  if (String(sim?.status ?? '').trim().toUpperCase() !== 'ISSUED') return null;
  const who = (label ?? '').trim() || 'ซิมนี้';
  const issue = sim?.current_stock_issue_number ? ` ${sim.current_stock_issue_number}` : '';
  const holder = sim?.issued_to_display ? ` (ผู้ยืม: ${sim.issued_to_display})` : '';
  return (
    `พบ ${who} แต่เลือกไม่ได้ — ซิมถูกยืมไปใช้ภายในตามใบเบิก${issue}${holder} · ` +
    'บันทึกรับคืนที่เมนู "คลังอุปกรณ์ › ใบเบิก-ยืม" ก่อน แล้วกลับมาเลือกใหม่'
  );
}

/* ==========================================================================
 * ซิมที่ถูกผูกกับเครื่องอยู่ (devices.sim_id) — BUG-S2164-001
 *
 * ฟอร์มสร้างการเช่าตั้งใจตัดซิมที่ผูกเครื่องออก (`!sim.device_id`) แต่ payload ของ /sims ไม่เคยมี
 * `device_id` — การผูกมาเป็น object `device` (schema sim.device) → เงื่อนไขเป็นจริงเสมอ = ตัวกรองตาย
 * ซิมที่เสียบอยู่ในเครื่องอื่นจึงโผล่ให้เลือกไปใส่อีกรายการได้ · อ่านทั้งสองรูปแบบที่นี่ที่เดียว
 * ========================================================================== */
export function isSimBoundToDevice(sim: { device_id?: number | null; device?: { id?: number | null } | null }): boolean {
  return Boolean(sim?.device_id) || Boolean(sim?.device && sim.device.id);
}
